/**
 * PAKET 1 (18 Aug 2026) — SINGLE SHARED API-ERROR READER
 *
 * Why this file exists (measured, not guessed):
 * The backend (`services/errors.py:http_exception_handler`) rewrites EVERY
 * HTTPException into `{ "error": <CODE>, "message": <detail> }` — there is NO
 * `detail` key in any error response. `api.ts` happened to have a `message`
 * fallback and therefore showed the real reason; `ebayPublish.ts` read ONLY
 * `data.detail`, got `undefined`, and fell back to the literal string
 * "Publish failed". So the bulk publish path lost eBay's message entirely
 * (proven byte-exact: the 500 body is 161 bytes and nginx logged `500 161`).
 *
 * Every client MUST read errors through this module so a third screen cannot
 * repeat the same mistake tomorrow.
 */

export interface ParsedApiError {
  /** Best human-readable text available. Never empty. */
  message: string;
  /** Application machine-readable code (e.g. "stock_unknown"), if present. */
  code?: string;
  /** Backend error code bucket (e.g. "SRV_INTERNAL_ERROR"). */
  errorCode?: string;
  /** First eBay ErrorCode found (e.g. "21917329"). */
  ebayErrorCode?: string;
  /** All eBay ErrorCodes when the response carried more than one. */
  ebayErrorCodes?: string[];
  /** eBay ErrorParameters[0] — the actual reason for multi-purpose codes (240). */
  ebayErrorDetail?: string;
  /** Structured payload (IS9: asin / amazon_qty / allowed_max). */
  details?: any;
  /** HTTP status when known. */
  status?: number;
  /** Full text for the technical-details block. */
  raw: string;
}

const EBAY_CODE_RE = /\[ErrorCode\s+(\d+)\]/g;

/**
 * Decode HTML entities that arrive verbatim from eBay's XML (`You&apos;ve`).
 * Measured: the backend never unescapes (html.unescape = 0 occurrences), there
 * is no double escaping, and every consumer renders this as a React text node
 * (no dangerouslySetInnerHTML) — so decoding is display-only and XSS-safe.
 */
export function decodeEntities(text: string): string {
  if (!text || text.indexOf("&") === -1) return text;
  return text
    .replace(/&apos;/g, "’")
    .replace(/&#39;/g, "’")
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    // &amp; last, otherwise "&amp;apos;" would decode twice
    .replace(/&amp;/g, "&");
}

function firstNonEmptyString(...vals: any[]): string | undefined {
  for (const v of vals) {
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return undefined;
}

/**
 * Parse a JSON error body into a ParsedApiError.
 * Lookup order: detail -> message -> error -> generic fallback.
 */
export function parseApiErrorBody(body: any, status?: number): ParsedApiError {
  const out: ParsedApiError = { message: "", raw: "" };
  out.status = status;

  if (typeof body === "string" && body.trim() !== "") {
    out.message = decodeEntities(body);
    out.raw = body;
    return out;
  }

  const b = body || {};
  const detail = b.detail;

  // 1) detail (string or object) — kept first for any endpoint/handler that
  //    still returns the FastAPI-native shape.
  let text: string | undefined;
  if (typeof detail === "string") {
    text = detail;
  } else if (detail && typeof detail === "object") {
    text = firstNonEmptyString(detail.message, detail.reason, detail.error);
    if (!text) {
      try {
        text = JSON.stringify(detail);
      } catch {
        text = undefined;
      }
    }
    if (typeof detail.code === "string" && detail.code) out.code = detail.code;
    out.details = detail;
  }

  // 2) message (current backend shape) -> 3) error bucket
  if (!text) text = firstNonEmptyString(b.message);
  if (typeof b.error === "string" && b.error) out.errorCode = b.error;
  if (!text) text = firstNonEmptyString(b.error);

  // Machine-readable fields added by PAKET 1 backend patch
  if (typeof b.code === "string" && b.code) out.code = b.code;
  if (b.details && typeof b.details === "object" && !out.details) out.details = b.details;
  // PAKET 2: dict detail backend tarafinda `str()` ile duzlestiriliyor
  // (errors.py:270) -> kullaniciya Python repr gidiyordu:
  //   "{'code': 'stock_unknown', 'message': 'Amazon stock is unknown...'}"
  // Ayni yanit `details` alaninda YAPISAL halini de tasiyor; repr tespit
  // edilirse okunabilir alt-mesaj tercih edilir. Backend DEGISMEDI.
  if (!text || /^\s*\{\s*['"]/.test(text)) {
    const structured = firstNonEmptyString(b.details && b.details.message, b.details && b.details.reason);
    if (structured) text = structured;
  }
  if (typeof b.ebay_error_code === "string" && b.ebay_error_code) out.ebayErrorCode = b.ebay_error_code;
  if (Array.isArray(b.ebay_error_codes) && b.ebay_error_codes.length) out.ebayErrorCodes = b.ebay_error_codes;
  if (typeof b.ebay_error_detail === "string" && b.ebay_error_detail) out.ebayErrorDetail = b.ebay_error_detail;

  const rawText = text || (status ? `HTTP ${status}` : "Unknown error");
  out.raw = rawText;
  out.message = decodeEntities(rawText);

  // Fallback extraction: older/other responses embed the code inside the text.
  if (!out.ebayErrorCode) {
    const codes: string[] = [];
    let m: RegExpExecArray | null;
    EBAY_CODE_RE.lastIndex = 0;
    while ((m = EBAY_CODE_RE.exec(rawText)) !== null) codes.push(m[1]);
    if (codes.length) {
      out.ebayErrorCode = codes[0];
      if (codes.length > 1) out.ebayErrorCodes = codes;
    }
  }
  if (!out.ebayErrorDetail && rawText.indexOf(" :: ") !== -1) {
    const p0 = rawText.split(" :: ")[1];
    if (p0 && p0.trim()) out.ebayErrorDetail = decodeEntities(p0.trim());
  }

  return out;
}

/**
 * PAKET 2 (18 Aug 2026) — USER-FACING TECHNICAL DETAIL
 *
 * The raw message is engineering plumbing:
 *   "Failed to publish: eBay AddFixedPriceItem failed: [ErrorCode 21917329] You've ..."
 * Three parts are noise for the user and are removed HERE ONLY (logs keep the raw
 * string untouched — nothing on the backend/log side is changed):
 *   1. "Failed to publish:"  -> the modal title already says it
 *   2. the eBay API method name -> mapped to a plain phrase. It also carried the
 *      word "Price" (AddFixedPriceItem), which readers mistook for a price problem.
 *   3. "[ErrorCode NNNNN]"   -> already shown as a label above the block
 */
const METHOD_PHRASE: Record<string, string> = {
  AddFixedPriceItem: "eBay rejected the listing:",
  ReviseFixedPriceItem: "eBay rejected the update:",
  EndFixedPriceItem: "eBay rejected the request to end the listing:",
};

/** Unknown/new eBay operations must still produce text — never an empty line. */
const METHOD_PHRASE_FALLBACK = "eBay rejected the request:";

// "eBay <Method> failed:"  (listing.py:981/1331)  |  "Trading <Method> HTTP 500:" (listing.py:870)
const METHOD_RE = /(?:eBay|Trading)\s+([A-Za-z][A-Za-z0-9-]*)\s+(?:failed|HTTP\s+\d+)\s*:\s*/;

export function simplifyTechnicalDetail(raw: string): string {
  if (!raw) return "";
  let t = decodeEntities(raw);

  t = t.replace(/^\s*Failed to publish:\s*/i, "");

  const m = t.match(METHOD_RE);
  if (m) {
    const phrase = METHOD_PHRASE[m[1]] || METHOD_PHRASE_FALLBACK;
    t = t.replace(METHOD_RE, phrase + " ");
  } else if (/^\s*eBay\s+.*\s+failed\s*:/i.test(t)) {
    // e.g. "eBay Multi-Variation listing failed:" — multi-word operation name
    t = t.replace(/^\s*eBay\s+.*?\s+failed\s*:\s*/i, METHOD_PHRASE_FALLBACK + " ");
  }

  t = t.replace(/\[ErrorCode\s+\d+\]\s*/g, "");
  return t.replace(/\s{2,}/g, " ").trim();
}

/**
 * Self-contained line stored on the draft (there is no ErrorCode label next to it),
 * so the code is appended in plain words. Never truncated.
 */
export function buildPersistedError(title: string, parsed: ParsedApiError): string {
  const detail = simplifyTechnicalDetail(parsed.raw || parsed.message);
  const code = parsed.ebayErrorCode ? ` (eBay ErrorCode ${parsed.ebayErrorCode})` : "";
  return detail ? `${title} — ${detail}${code}` : `${title}${code}`;
}

/** Error object carrying the parsed payload (thrown by api.ts / ebayPublish.ts). */
export interface ApiErrorLike extends Error {
  status?: number;
  code?: string;
  detail?: any;
  apiError?: ParsedApiError;
}

/** Attach parsed fields onto an Error so catch blocks can read them directly. */
export function buildApiError(parsed: ParsedApiError): ApiErrorLike {
  const e: ApiErrorLike = new Error(parsed.message);
  e.status = parsed.status;
  e.code = parsed.code;
  e.detail = parsed.details;
  e.apiError = parsed;
  return e;
}

/**
 * Read a ParsedApiError out of whatever a catch block received.
 * Works for errors thrown by api.ts / ebayPublish.ts (which attach `apiError`),
 * for axios-shaped errors, and for plain Errors.
 */
export function parseCaughtError(err: any): ParsedApiError {
  if (err && err.apiError && typeof err.apiError === "object") return err.apiError as ParsedApiError;

  const axiosBody = err?.response?.data;
  if (axiosBody) return parseApiErrorBody(axiosBody, err?.response?.status ?? err?.status);

  const msg = firstNonEmptyString(err?.message, typeof err === "string" ? err : undefined) || String(err ?? "Unknown error");
  const parsed = parseApiErrorBody({ message: msg }, err?.status);
  if (typeof err?.code === "string" && err.code && !parsed.code) parsed.code = err.code;
  return parsed;
}
